import { inject } from 'helpers/mobx'
import React from 'react'

import Flyout from 'components/SearchBar/SearchbarFlyout'
import { debounce } from 'helpers/delays'
import SearchBarStore from 'store/features/searchBar'
import styled from 'styled-components'
import { granite } from 'uiComponents/constants/colors'

interface IProps {
  searchBarStore: SearchBarStore
  translatedSearchPlaceholder: string
  trackSearchEvent: ({
    type,
    searchTerm,
  }: {
    type: string
    searchTerm: string
  }) => void
  updateSearchContext?: (term: string) => void
}

const DEBOUNCE_DELAY_MS = 500

const SearchBarWrapper = styled.div`
  height: 40px;
  width: 100%;
`

const TextInputWrapper = styled.div`
  position: relative;
`

const TextInput = styled.input`
  background-color: #f4f4f5;
  display: inline-block;
  font-size: 13px;
  font-weight: 300;
  height: 40px;
  line-height: 1.54;
  padding-left: 52px;
  padding-right: 52px;
  width: 100%;

  &::placeholder {
    color: ${granite};
  }
`

class SearchBar extends React.Component<IProps, any> {
  private inputRef
  private fetchSuggestions
  private wrapperRef: HTMLElement | null

  componentDidMount() {
    const { searchBarStore } = this.props

    this.attachDOMEvents()

    this.fetchSuggestions = debounce(
      () => searchBarStore.fetchBrandSuggestions(searchBarStore.searchQuery),
      DEBOUNCE_DELAY_MS,
    )

    this.props.searchBarStore.getLastsSearches()
  }

  componentWillUnmount() {
    this.detachDOMEvents()
  }

  hasDocument() {
    return !(typeof document === 'undefined' || !document.createElement)
  }

  attachDOMEvents() {
    if (!this.hasDocument()) {
      return false
    }

    document.addEventListener('click', this.handleDocumentClick, false)
    document.addEventListener('touchend', this.handleDocumentClick, false)
  }

  detachDOMEvents() {
    if (!this.hasDocument()) {
      return false
    }

    document.removeEventListener('click', this.handleDocumentClick, false)
    document.removeEventListener('touchend', this.handleDocumentClick, false)
  }

  handleWrapperClick = () => {
    const { searchBarStore } = this.props
    if (!searchBarStore.isSearchBarOpen) {
      this.toggleSearchBar()
    }
  }

  handleDocumentClick = e => {
    const { searchBarStore } = this.props
    const target = e.target

    if (
      searchBarStore.isSearchBarOpen &&
      this.wrapperRef &&
      !this.wrapperRef.contains(target)
    ) {
      searchBarStore.closeFlyout()
      if (!searchBarStore.searchQuery.length) {
        this.toggleSearchBar()
      }
    }
  }

  setInputRef = inputRef => {
    this.inputRef = inputRef
  }

  setWrapperRef = ref => {
    this.wrapperRef = ref
  }

  focusInput = () => {
    const { searchBarStore } = this.props

    if (!searchBarStore.isSearchBarOpen) {
      return
    }

    this.inputRef.focus()
    if (searchBarStore.isSearchBarOpen) {
      searchBarStore.getLastsSearches()
    }
  }

  toggleSearchBar = () => {
    const { searchBarStore } = this.props
    searchBarStore.toggleSearchBar()

    if (!searchBarStore.isSearchBarOpen) {
      searchBarStore.closeFlyout()
    }

    this.focusInput()
  }

  handleTextInputSubmit = async event => {
    const { searchBarStore } = this.props
    event.preventDefault()
    this.props.trackSearchEvent({
      type: 'submitted',
      searchTerm: this.inputRef.value,
    })

    this.props.updateSearchContext?.(this.inputRef.value)

    searchBarStore.submitQuery(this.inputRef)
  }

  handleTextInputChange = event => {
    const { searchBarStore } = this.props

    if (!searchBarStore.isFlyoutOpen) {
      searchBarStore.openFlyout()
    }

    searchBarStore.setSearchQuery(event.target.value)
    searchBarStore.searchQuery.trim().length < 3 &&
      searchBarStore.clearSuggestions()
    searchBarStore.searchQuery.trim().length >= 3 && this.fetchSuggestions()
  }

  handleCloseIconClick = () => {
    const { searchBarStore } = this.props

    searchBarStore.clearSuggestions()

    if (searchBarStore.isSearchBarOpen && searchBarStore.searchQuery.length) {
      searchBarStore.setSearchQuery('')
      this.inputRef.focus()
      return
    }
    searchBarStore.closeFlyout()
    this.toggleSearchBar()
  }

  handleElementClick = elementTitle => {
    this.props.searchBarStore.setSearchQuery(elementTitle)
    this.props.trackSearchEvent({
      type: 'suggestion',
      searchTerm: elementTitle,
    })
    this.props.searchBarStore.closeFlyout()
  }

  renderSearchesFlyout = () => {
    const { searchBarStore } = this.props

    if (
      searchBarStore.searchQuery.trim().length < 3 &&
      searchBarStore.searchTerms.terms.length
    ) {
      return (
        <Flyout
          isOpen={searchBarStore.isFlyoutOpen}
          onElementClick={this.handleElementClick}
          lastSearches={searchBarStore.searchTerms.terms}
          isNotFoundStyle
        />
      )
    }

    if (
      searchBarStore.searchQuery.trim().length >= 3 &&
      searchBarStore.suggestedSearches.products.length
    ) {
      return (
        <Flyout
          isOpen={searchBarStore.isFlyoutOpen}
          onElementClick={this.handleElementClick}
          searchQuery={searchBarStore.searchQuery}
          suggestions={searchBarStore.suggestedSearches}
          isNotFoundStyle
        />
      )
    }

    if (
      searchBarStore.searchQuery.trim().length >= 3 &&
      searchBarStore.brandSuggestions.brands.length
    ) {
      return (
        <Flyout
          isOpen={searchBarStore.isFlyoutOpen}
          onElementClick={this.handleElementClick}
          searchQuery={searchBarStore.searchQuery}
          suggestions={searchBarStore.brandSuggestions}
          isNotFoundStyle
        />
      )
    }
  }

  render() {
    const { searchBarStore, translatedSearchPlaceholder } = this.props

    return (
      <SearchBarWrapper
        data-testid="SearchBar"
        onClick={this.handleWrapperClick}
        ref={this.setWrapperRef}
      >
        <form onSubmit={this.handleTextInputSubmit}>
          <TextInputWrapper>
            <TextInput
              type="text"
              ref={this.setInputRef}
              value={searchBarStore.searchQuery}
              onFocus={this.props.searchBarStore.openFlyout}
              onChange={this.handleTextInputChange}
              placeholder={translatedSearchPlaceholder}
            />
            {searchBarStore.searchQuery.length > 0 && (
              <CloseIcon onClick={this.handleCloseIconClick} />
            )}
            <SearchIcon onClick={this.handleTextInputSubmit} />
          </TextInputWrapper>
        </form>

        {this.renderSearchesFlyout()}
      </SearchBarWrapper>
    )
  }
}

const SearchIcon = styled.div`
  background: url(${require('assets/icons/svgs/magnifier.svg')});
  background-size: cover;
  content: '';
  cursor: pointer;
  display: inline-block;
  position: absolute;
  width: 23px;
  height: 21px;
  top: 11px;
  left: 20px;
`

const CloseIcon = styled.div`
  background: url(${require('assets/icons/svgs/close-light.svg')});
  content: '';
  cursor: pointer;
  display: inline-block;
  position: absolute;
  right: 20px;
  width: 10px;
  height: 10px;
  top: 15px;
`

export default inject(
  store => ({
    searchBarStore: store.notFoundSearchBarStore,
    translatedSearchPlaceholder: store.t('Nach einer Marke suchen'),
    trackSearchEvent: store.trackingStore.trackSearchEvent,
  }),
  { observe: true },
)(SearchBar)
